import { ModuleRegistry } from "ag-grid-community";
import {
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MasterDetailModule,
  MultiFilterModule,
  PivotModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  SetFilterModule,
  SideBarModule,
  TreeDataModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { forwardRef, useCallback, useEffect, useMemo, useRef } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { columnTypesBoolean } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesBoolean";
import { columnTypesDate } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { columnTypesDollar } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesDollar";
import { columnTypesPercent } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesPercent";
import AgGridTableWrapper from "../AgGridTableWrapper";

ModuleRegistry.registerModules([
  SideBarModule,
  RowGroupingPanelModule,
  MultiFilterModule,
  RowGroupingModule,
  PivotModule,
  TreeDataModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  MasterDetailModule,
  SetFilterModule,
]);

const AgGridClickableTable = forwardRef(
  (
    {
      rowData,
      columnDefs,
      quickFilterText,
      height,
      selectedId,
      path,
      simpleTable = true,
      gridOptions,
    },
    ref
  ) => {
    const { id: loanId } = useParams();
    const navigate = useNavigate();
    const gridApiRef = useRef(null);

    const handleOnRowClicked = useCallback(
      (params) => {
        if (params.node.detail) return;

        const dataId = params.data?._id;
        if (dataId === selectedId && selectedId) {
          navigate(`/loan/${loanId}/${path}`);
        } else {
          navigate(`/loan/${loanId}/${path}/${dataId}`);
        }
      },
      [selectedId, path, loanId, navigate]
    );

    const onRowDataChanged = useCallback(
      (params) => {
        if (!selectedId) {
          params.api.forEachNode((node) => node.setExpanded(false));
        } else {
          params.api.forEachNode((node) =>
            node.setExpanded(node.data?._id === selectedId)
          );
        }
      },
      [selectedId]
    );

    useEffect(() => {
      if (!gridApiRef.current) return;
      gridApiRef.current.forEachNode((node) => {
        node.setExpanded(node.data?._id === selectedId);
      });
    }, [selectedId]);

    const _columnDefs = useMemo(() => columnDefs, [columnDefs]);

    const defaultColDef = useMemo(
      () => ({
        filter: true,
        enableRowGroup: true,
        enablePivot: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        // headerCheckboxSelectionFilteredOnly: true,
        menuTabs: ["filterMenuTab"],
      }),
      []
    );

    const columnTypes = useMemo(
      () => ({
        ...columnTypesDate(),
        ...columnTypesPercent(),
        ...columnTypesDollar(),
        ...columnTypesBoolean(),
      }),
      []
    );

    const getRowStyle = useCallback(
      (params) =>
        params?.data?._id === selectedId
          ? {
              fontWeight: 700,
              background: "#eff7fe",
            }
          : null,
      [selectedId]
    );

    const onFirstDataRendered = useCallback(
      (params) => {
        params.api.forEachNode((node) => {
          node.setExpanded(node.data?._id === selectedId);
        });
      },
      [selectedId]
    );

    return (
      <AgGridTableWrapper
        suppressMinHeightWhenPopulated={rowData}
        height={height}
        simpleTable={simpleTable}
        sx={{ minWidth: "340px" }}
      >
        <AgGridReact
          ref={ref}
          rowData={rowData}
          columnDefs={_columnDefs}
          defaultColDef={defaultColDef}
          quickFilterText={quickFilterText}
          getRowStyle={getRowStyle}
          stopEditingWhenCellsLoseFocus
          onFirstDataRendered={onFirstDataRendered}
          tooltipShowDelay={0}
          tooltipInteraction={true}
          tooltipMouseTrack
          columnTypes={columnTypes}
          allowDragFromColumnsToolPanel={false}
          gridOptions={gridOptions}
          domLayout={"autoHeight"}
          keepDetailRows={true}
          detailCellRendererParams={{ refreshStrategy: "rows" }}
          detailRowAutoHeight={true}
          onRowDataChanged={onRowDataChanged}
          onRowClicked={handleOnRowClicked}
        />
      </AgGridTableWrapper>
    );
  }
);

export default AgGridClickableTable;
